import { TrainTrack } from 'lucide-react';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useSonner } from 'sonner';

import type { LayoutContextType } from '@/components/layout/main-layout';
import type { ToggleItem, ToggleItemValue } from '@/types';

import MapIconSvg from '@/assets/icons/map.svg?react';
import BottomNav from '@/components/bottom-nav';
import ItemDrawer from '@/components/drawers/item-drawer';
import SlowSectionDrawer from '@/components/drawers/item-drawer/slow-section-drawer';
import Map from '@/components/map';
import FloatingButton from '@/components/map/floating-button';
import InterfaceStatusButton from '@/components/map/interface-status-button';
import LadderLoader from '@/components/shared/loader/ladder-loader';
import Spinner from '@/components/shared/spinner';
import StartView from '@/components/start-view';
import { cn } from '@/lib/utils';
import useReleaseNoteStore from '@/store/useReleaseNoteStore';
import {
  SEARCH_PARAM_DRAWER,
  SEARCH_PARAM_LOCK_PIN,
  SEARCH_PARAM_LOCK_PIN_TYPE,
  SEARCH_PARAM_SECTION_ID,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_SLOW_SECTION_ID,
  SEARCH_PARAM_SLOW_SECTIONS,
  SEARCH_PARAM_VIEW,
  VIEW_LADDER,
} from '@/utils/constants/queries';

const LazyLadderView = lazy(() => import('@/components/ladder-view'));

const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toasts } = useSonner();

  const { setIsTopNavVisible } = useOutletContext<LayoutContextType>() || {
    setIsTopNavVisible: () => {},
  };

  const [isAnimationFinished, setIsAnimationFinished] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState<number>(0);
  const currentView = searchParams.get(SEARCH_PARAM_VIEW) || 'map';
  const isLadderView = currentView === VIEW_LADDER;
  const [isLadderViewOpened, setIsLadderViewOpened] = useState(isLadderView);
  const { isReleaseToastVisible, setIsAppLoading } = useReleaseNoteStore();

  const lockPinId = searchParams.get(SEARCH_PARAM_LOCK_PIN);
  const lockType = searchParams.get(SEARCH_PARAM_LOCK_PIN_TYPE);
  const isPinned = !!lockPinId;

  const slowSectionId = searchParams.get(SEARCH_PARAM_SLOW_SECTION_ID);
  const isSlowSectionOpen = !!slowSectionId;
  const minimumToastCount = isReleaseToastVisible ? 2 : 1;
  const isToastVisible = toasts.length >= minimumToastCount;

  const isDrawerOpen = (isPinned && !!searchParams.get(SEARCH_PARAM_DRAWER)) || isSlowSectionOpen;

  const showSpinner = !isMapReady && !isLadderView;
  const showApp = isMapReady || isLadderView;

  const isLoading = !isAnimationFinished || showSpinner;

  useEffect(() => {
    setIsAppLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    setIsTopNavVisible(!isLoading);

    return () => {
      setIsTopNavVisible(true);
    };
  }, [isLoading, setIsTopNavVisible]);

  useEffect(() => {
    if (!isDrawerOpen || isLadderView) {
      setDrawerHeight(0);
    }
  }, [isDrawerOpen, isLadderView]);

  const viewToggleItems: ToggleItem[] = [
    { value: 'route', label: 'Route View', icon: TrainTrack },
    { value: 'map', label: 'Map View', icon: MapIconSvg },
  ];

  const handleAnimationFinish = () => {
    setIsAnimationFinished(true);
  };

  const handleMapReady = useCallback(() => {
    setIsMapReady(true);
  }, []);

  const handleSlowSectionClose = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(SEARCH_PARAM_SLOW_SECTION_ID);
      prev.delete(SEARCH_PARAM_SLOW_SECTIONS);
      prev.delete(SEARCH_PARAM_SHOW);
      prev.delete(SEARCH_PARAM_VIEW);
      prev.delete(SEARCH_PARAM_SECTION_ID);

      if (isPinned && !!lockType) {
        prev.set(SEARCH_PARAM_LOCK_PIN, lockPinId);
        prev.set(SEARCH_PARAM_LOCK_PIN_TYPE, lockType);
        prev.set(SEARCH_PARAM_DRAWER, 'true');
      }

      return prev;
    });
  }, [lockPinId, lockType, isPinned]);

  const handleViewChange = (newView: ToggleItemValue) => {
    const newParams = new URLSearchParams(searchParams);

    if (newView === 'route') {
      setIsLadderViewOpened(true);
      newParams.set(SEARCH_PARAM_VIEW, VIEW_LADDER);
    } else {
      newParams.delete(SEARCH_PARAM_VIEW);
      newParams.delete(SEARCH_PARAM_SECTION_ID);
    }

    setSearchParams(newParams);
  };

  if (!isAnimationFinished) {
    return <StartView onFinish={handleAnimationFinish} />;
  }

  return (
    <div
      className={cn('w-dvw h-[calc(100dvh-64px)] absolute', { 'overflow-hidden': isLadderView })}
    >
      {showSpinner && <Spinner />}
      <div className={cn({ invisible: !showApp })}>
        <main>
          <Suspense fallback={<LadderLoader />}>
            {isLadderViewOpened && (
              <LazyLadderView isActive={isLadderView} isToastVisible={isToastVisible} />
            )}
          </Suspense>

          <Map
            shouldRenderMap={!isLadderView}
            isMapVisible={showApp}
            onMapReady={handleMapReady}
            isMapReady={isMapReady}
          />

          <FloatingButton
            items={viewToggleItems}
            activeValue={isLadderView ? 'route' : 'map'}
            onValueChange={handleViewChange}
            isDrawerOpen={isDrawerOpen}
            isToastVisible={isToastVisible}
            drawerHeight={drawerHeight}
          />
          {showApp && (
            <InterfaceStatusButton
              isDrawerOpen={isDrawerOpen}
              isToastVisible={isToastVisible}
              drawerHeight={drawerHeight}
            />
          )}
        </main>
        {showApp && <BottomNav />}
        {showApp && isDrawerOpen && !isLadderView && !isSlowSectionOpen && (
          <ItemDrawer isToastVisible={isToastVisible} onHeightChange={setDrawerHeight} />
        )}
        {showApp && !isLadderView && (
          <SlowSectionDrawer
            slowSectionId={slowSectionId}
            onClose={handleSlowSectionClose}
            isToastVisible={isToastVisible}
            onHeightChange={isSlowSectionOpen ? setDrawerHeight : undefined}
          />
        )}
      </div>
    </div>
  );
};

export default Home;
